import {
  GeneratedSkedApiClient,
  GeneratedSkedApiError,
} from "../generated-client";
import type { paths } from "../generated/sked-api-openapi-types";
import {
  logUploadBreadcrumb,
  logUploadFailure,
  withUploadSpan,
} from "./upload-observability";
import { resolveMediaCorrelationKey } from "../../utils/media-observability";
import { isUploadAbortedError, UploadAbortedError } from "./types";

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();
const DEFAULT_TRANSCODE_POLL_INTERVAL_MS = 1_000;
const BUSY_TRANSCODE_POLL_INTERVAL_MS = 2_000;
const CROWDED_TRANSCODE_POLL_INTERVAL_MS = 3_000;
const MEDIACONVERT_POLL_INTERVAL_MS = 5_000;
const TRANSIENT_STATUS_RETRY_DELAYS_MS = [5_000, 10_000, 20_000];
// Matches the allowance the provider gets in
// apps/sked-api/src/bin/mediaconvert-transcode.js.
const TRANSCODE_DEADLINE_MS = 30 * 60 * 1000;

let activeTranscodeJobs = 0;

export function getTranscodePollIntervalMs(
  provider: string | undefined,
  activeJobs: number
): number {
  if (provider === "mediaconvert") {
    return MEDIACONVERT_POLL_INTERVAL_MS;
  }

  if (activeJobs <= 2) {
    return DEFAULT_TRANSCODE_POLL_INTERVAL_MS;
  }

  return activeJobs <= 4
    ? BUSY_TRANSCODE_POLL_INTERVAL_MS
    : CROWDED_TRANSCODE_POLL_INTERVAL_MS;
}

export async function transcodeVideoByUrl(
  url: string,
  {
    onProgress,
    uploadAttemptId,
    mediaCorrelationKey,
    fileName,
    signal,
  }: {
    onProgress(progress: number): void;
    uploadAttemptId: string;
    mediaCorrelationKey?: string;
    fileName?: string;
    signal?: AbortSignal;
  }
): Promise<string> {
  const metadata = {
    url,
    uploadAttemptId,
    fileName,
    mediaCorrelationKey:
      mediaCorrelationKey ??
      resolveMediaCorrelationKey({ uploadAttemptId, urls: [url] }),
  };

  logUploadBreadcrumb("media_upload.transcode.start", metadata);

  return withUploadSpan("media_upload.transcode", metadata, async () => {
    try {
      const transcodeJob = await generatedSkedApiClient.unwrap(
        await generatedSkedApiClient.client.POST(
          "/content-inbox/transcode/start",
          {
            signal,
            body: {
              url,
              observability: {
                uploadAttemptId,
                mediaCorrelationKey: metadata.mediaCorrelationKey,
                fileName,
              },
            },
          }
        )
      );
      const jobId = transcodeJob.id;

      if (jobId === undefined) {
        throw new Error("Transcoding job response did not include an id");
      }

      logUploadBreadcrumb("media_upload.transcode.job_created", {
        ...metadata,
        jobId,
      });

      const transcode = await pollTranscodeJob({
        jobId,
        provider: transcodeJob.provider,
        signal,
        onProgress,
        metadata,
      });

      if (transcode.status !== "completed") {
        throw new Error(
          transcode.status
            ? `Transcoding failed with status: ${transcode.status}`
            : "Transcoding failed"
        );
      }

      logUploadBreadcrumb("media_upload.transcode.complete", {
        ...metadata,
        jobId,
        progress: transcode.progress,
        status: transcode.status,
      });

      return jobId;
    } catch (error) {
      // A cancelled request rejects with the platform's own abort error, which
      // would otherwise be logged as a transcode failure.
      if (signal?.aborted) {
        throw new UploadAbortedError("Transcoding aborted");
      }

      if (!isUploadAbortedError(error)) {
        logUploadFailure("media_upload.transcode.failure", error, metadata);
      }
      throw error;
    }
  });
}

async function pollTranscodeJob({
  jobId,
  provider,
  signal,
  onProgress,
  metadata,
}: {
  jobId: string;
  provider: string | undefined;
  signal?: AbortSignal;
  onProgress(progress: number): void;
  metadata: Record<string, unknown>;
}): Promise<JobStatus> {
  let lastLoggedProgressBucket = -1;
  const deadline = Date.now() + TRANSCODE_DEADLINE_MS;

  activeTranscodeJobs += 1;
  try {
    while (true) {
      if (Date.now() > deadline) {
        throw new Error(
          "Transcoding took too long, please try uploading this video again"
        );
      }

      await wait(
        getTranscodePollIntervalMs(provider, activeTranscodeJobs),
        signal
      );

      if (signal?.aborted) {
        throw new UploadAbortedError("Transcoding aborted");
      }

      const transcode = await getJobStatusWithRetry(jobId, signal);
      const currentBucket = Math.floor(transcode.progress / 10);
      if (currentBucket > lastLoggedProgressBucket) {
        logUploadBreadcrumb("media_upload.transcode.progress", {
          ...metadata,
          jobId,
          progress: transcode.progress,
          status: transcode.status,
        });
        lastLoggedProgressBucket = currentBucket;
      }
      onProgress(transcode.progress);

      if (transcode.status !== "processing") {
        return transcode;
      }
    }
  } finally {
    activeTranscodeJobs -= 1;
  }
}

async function getJobStatusWithRetry(
  jobId: string,
  signal?: AbortSignal
): Promise<JobStatus> {
  let retry = 0;
  while (true) {
    try {
      return await getJobStatus(jobId, signal);
    } catch (error) {
      const delay = TRANSIENT_STATUS_RETRY_DELAYS_MS[retry];
      if (!isTransientStatusError(error) || delay === undefined) throw error;
      retry += 1;
      await wait(delay, signal);
    }
  }
}

function isTransientStatusError(error: unknown): boolean {
  return (
    error instanceof GeneratedSkedApiError &&
    (error.status === 429 || error.status === 503)
  );
}

async function getJobStatus(
  jobId: string,
  signal?: AbortSignal
): Promise<JobStatus> {
  const data = await generatedSkedApiClient.unwrap(
    await generatedSkedApiClient.client.GET(
      "/content-inbox/transcode/{jobId}",
      {
        signal,
        params: {
          path: { jobId: String(jobId) },
        },
      }
    )
  );
  const outputUrls = (data.output_urls ?? {}) as TranscodeOutputUrls;

  const urlKey = Object.keys(outputUrls).find((key) => key.startsWith("mp4"));
  const thumbnailKey = Object.keys(outputUrls).find(
    (key) => key.startsWith("jpg") && key.endsWith("#start")
  );

  return {
    status: data.status ?? "",
    progress: Number.parseInt(data.progress ?? "0", 10),
    url: (urlKey && getFirstOutputUrl(outputUrls[urlKey])) ?? "",
    thumbnail:
      (thumbnailKey && getFirstOutputUrl(outputUrls[thumbnailKey])) ?? "",
  };
}

function getFirstOutputUrl(value: TranscodeOutputUrls[string]): string {
  if (Array.isArray(value)) {
    return value[0] ?? "";
  }

  return value ?? "";
}

async function wait(ms: number, signal?: AbortSignal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError("Transcoding aborted"));
      return;
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;

    function handleAbort() {
      clearTimeout(timeout);
      reject(new UploadAbortedError("Transcoding aborted"));
    }

    // Registered before the timer so an abort raised while the timer is being
    // scheduled still settles the promise instead of hanging it.
    signal?.addEventListener("abort", handleAbort, { once: true });

    timeout = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve(undefined);
    }, ms);
  });
}

interface JobStatus {
  progress: number;
  status: string;
  url: string;
  thumbnail: string;
}

type TranscodeOutputUrls = Record<string, string | string[] | undefined>;
